import React, { useState, useEffect, useCallback } from 'react';
import Tooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import { Input } from '@pwc/appkit-react/lib/Input';
import { Button } from '@pwc/appkit-react/lib/Button';
import { GrLink, GrUnlink } from 'react-icons/gr';

import { FormikPropTypes } from '../../shared/forms/propTypes';
import { RequiresCommands } from '../../shared/authorization/requiresPermission.component';

const divide = (a, b) => (b !== 0 ? a / b : 0).toFixed(2);
const multiply = (a, b) => (a * b).toFixed(2);

const LinkIcon = () => <div className="link-button-icon-link"><GrLink size={20} /></div>;
const UnlinkIcon = () => <div className="link-button-icon-unlink"><GrUnlink size={20} /></div>;
const Separator = () => <div className="amounts-separator" />;

const LINK_TOOLTIP = 'Amounts are linked. You may edit either amount, the other will be calculated automatically.';
const UNLINK_TOOLTIP = 'Amounts are not linked. You may edit the amounts separately.';

const LinkButton = ({ isLinked, callback }) => (
  <div className="link-button" data-tip={isLinked ? LINK_TOOLTIP : UNLINK_TOOLTIP}>
    <Button kind="transparent" onClick={callback}>
      { isLinked ? <LinkIcon /> : <UnlinkIcon /> }
    </Button>
    <div className="link-button-label">{ isLinked ? 'Link' : 'Unlink' }</div>
  </div>
);

const getStateApportionmentRate = (stateApportionmentRate, isUsState) =>
  !isUsState || (!stateApportionmentRate && stateApportionmentRate !== 0)
    ? 1
    : stateApportionmentRate;

const AmountFormik = ({
  formik,
  name,
  value: { net, gross },
  suffix = '',
  deferredTaxRate,
  stateApportionmentRate,
  allowGross = false,
  isUsState = false,
  className = '',
}) => {
  const netGrossRate = deferredTaxRate * getStateApportionmentRate(stateApportionmentRate, isUsState);

  const [isLinked, setIsLinked] = useState(true);

  const toggleLink = () => setIsLinked(!isLinked);

  useEffect(() => {
    if (allowGross) {
      formik.setFieldValue(name, { net, gross: divide(net, netGrossRate) });
    } else {
      formik.setFieldValue(name, { net, gross: '' });
    }
  }, [allowGross, deferredTaxRate, isLinked]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeNet = !isLinked
    ? net => formik.setFieldValue(name, { net, gross })
    : net => {
      const gross = divide(net, netGrossRate);
      formik.setFieldValue(name, { net, gross });
    };

  const handleChangeGross = !isLinked
    ? gross => formik.setFieldValue(name, { net, gross })
    : gross => {
      const net = multiply(gross, netGrossRate);
      formik.setFieldValue(name, { net, gross });
    };

  const handleChangeSingle = net => {
    formik.setFieldValue(name, { net, gross: '' });
  };

  const handleBlur = useCallback(() => {
    const { amount } = formik.values;

    const net = isNaN(amount.net) ? 0 : Number(amount.net);
    const gross = isNaN(amount.gross) ? 0 : Number(amount.gross);

    if (allowGross) {
      formik.setFieldValue(name, {
        net: net.toFixed(2),
        gross: gross.toFixed(2),
      });
    } else {
      formik.setFieldValue(name, {
        net: net.toFixed(2),
        gross: '',
      });
    }
  }, [allowGross, formik, name]); // eslint-disable-line react-hooks/exhaustive-deps

  const error = getIn(formik.errors, name);
  const netError = error && error.net;
  const grossError = error && error.gross;

  const touched = getIn(formik.touched, name);
  const netTouched = touched && touched.net;
  const grossTouched = touched && touched.gross;

  return (
    <div className={`amount-control ${className}`}>
      <Input
        type="text"
        size="sm"
        label="Net Amount"
        suffix={suffix}
        value={net}
        onChange={allowGross ? handleChangeNet : handleChangeSingle}
        errMsg={netError}
        hasError={netError && netTouched}
        onBlur={handleBlur}
      />
      {allowGross && (
        <>
          <RequiresCommands commands="canUnlinkNetGrossAmount" otherwise={Separator}>
            <LinkButton
              isLinked={isLinked}
              callback={toggleLink} // eslint-disable-line react/jsx-no-bind
            />
          </RequiresCommands>
          <Input
            type="text"
            size="sm"
            label="Gross Amount"
            suffix={suffix}
            value={gross}
            onChange={handleChangeGross} // eslint-disable-line react/jsx-no-bind
            errMsg={grossError}
            hasError={grossError && grossTouched}
            onBlur={handleBlur}
          />

          <Tooltip delayShow={300} effect="solid" className="amount-control-tooltip" />
        </>
      )}
    </div>
  );
};

LinkButton.propTypes = {
  isLinked: PropTypes.bool.isRequired,
  callback: PropTypes.func.isRequired,
};

AmountFormik.propTypes = {
  formik: FormikPropTypes,
  name: PropTypes.string.isRequired,
  suffix: PropTypes.string,
  value: PropTypes.shape({
    net: PropTypes.string.isRequired,
    gross: PropTypes.string,
  }).isRequired,
  deferredTaxRate: PropTypes.number.isRequired,
  stateApportionmentRate: PropTypes.number.isRequired,
  allowGross: PropTypes.bool,
  isUsState: PropTypes.bool,
  className: PropTypes.string,
};

export default AmountFormik;
